import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

interface LeaderboardEntry {
  rank: number;
  team: string;
  idea: string;
  score: number;
  round1: number;
  round2: number;
  round3: number;
}

export function Leaderboard() {
  const [selectedHackathon, setSelectedHackathon] = useState("1");
  const [isPublished, setIsPublished] = useState(false);
  const [notes, setNotes] = useState("");

  const [leaderboard] = useState<LeaderboardEntry[]>([
    {
      rank: 1,
      team: "Team Alpha",
      idea: "AI-Powered Healthcare Assistant",
      score: 95.5,
      round1: 32,
      round2: 33,
      round3: 30.5,
    },
    {
      rank: 2,
      team: "Team Beta",
      idea: "Smart Energy Management System",
      score: 92.0,
      round1: 30,
      round2: 32,
      round3: 30,
    },
    {
      rank: 3,
      team: "Team Gamma",
      idea: "Blockchain-Based Supply Chain",
      score: 88.5,
      round1: 29,
      round2: 30,
      round3: 29.5,
    },
    {
      rank: 4,
      team: "Team Delta",
      idea: "Automated Trading Platform",
      score: 85.0,
      round1: 28,
      round2: 29,
      round3: 28,
    },
    {
      rank: 5,
      team: "Team Epsilon",
      idea: "Virtual Reality Learning Platform",
      score: 82.5,
      round1: 27,
      round2: 28,
      round3: 27.5,
    },
  ]);

  const handlePublish = () => {
    setIsPublished(!isPublished);
    console.log("Leaderboard published:", !isPublished);
  };

  return (
    <div className="max-w-[1200px] px-[12px] py-[32px]">
      <div className="mb-[32px]">
        <h1 className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[42px] text-[#1a2332] text-[28px]">
          Leaderboard
        </h1>
        <p className="font-['Inter:Regular',sans-serif] font-normal leading-[21px] text-[#6e7891] text-[14px]">
          View rankings, scores, and publish final leaderboard.
        </p>
      </div>

      {/* Controls */}
      <div
        className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] p-[24px] mb-[24px]"
        style={{
          backgroundImage:
            "linear-gradient(157.593deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 100%)",
        }}
      >
        <div className="grid grid-cols-2 gap-[24px]">
          <div className="flex flex-col gap-[8px]">
            <label className="font-['Inter:Medium',sans-serif] font-medium leading-[19.5px] text-[#1a2332] text-[13px]">
              Select Hackathon
            </label>
            <Select value={selectedHackathon} onValueChange={setSelectedHackathon}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-50">
                <SelectItem value="1" className="hover:bg-gray-200 hover:cursor-pointer">AI Innovation Challenge 2026</SelectItem>
                <SelectItem value="2" className="hover:bg-gray-200 hover:cursor-pointer">Sustainable Tech Hackathon</SelectItem>
                <SelectItem value="3" className="hover:bg-gray-200 hover:cursor-pointer">FinTech Innovation Sprint</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-[8px]">
            <Label className="font-['Inter:Medium',sans-serif] font-medium leading-[19.5px] text-[#1a2332] text-[13px]">
              Visibility
            </Label>
            <div className="flex items-center gap-[12px] h-[40px]">
              <Switch checked={isPublished} onCheckedChange={handlePublish} />
              <span className="font-['Inter:Medium',sans-serif] font-medium text-[14px] text-[#4a5068]">
                {isPublished ? "Published" : "Draft"}
              </span>
              {isPublished && (
                <span className="px-[12px] py-[4px] rounded-[8px] bg-[#00d68f] font-['Inter:Medium',sans-serif] font-medium text-[11px] text-white">
                  Live
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-[20px]">
          <Label className="font-['Inter:Medium',sans-serif] font-medium leading-[19.5px] text-[#1a2332] text-[13px] mb-[8px] block">
            Notes
          </Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about the leaderboard (optional)"
            rows={3}
          />
        </div>
      </div>

      {/* Leaderboard Table */}
      <div
        className="rounded-[16px] border-[0.667px] border-[rgba(228,236,244,0.4)] shadow-[0px_8px_32px_0px_rgba(31,38,135,0.08)] overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(157.593deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.6) 100%)",
        }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#f5f7fb] border-b border-[#e4e8ee]">
              <tr>
                <th className="px-[24px] py-[16px] text-center font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[13px] uppercase tracking-[0.275px]">
                  Rank
                </th>
                <th className="px-[24px] py-[16px] text-left font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[13px] uppercase tracking-[0.275px]">
                  Team
                </th>
                <th className="px-[24px] py-[16px] text-left font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[13px] uppercase tracking-[0.275px]">
                  Idea
                </th>
                <th className="px-[24px] py-[16px] text-center font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[13px] uppercase tracking-[0.275px]">
                  Round 1
                </th>
                <th className="px-[24px] py-[16px] text-center font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[13px] uppercase tracking-[0.275px]">
                  Round 2
                </th>
                <th className="px-[24px] py-[16px] text-center font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[13px] uppercase tracking-[0.275px]">
                  Round 3
                </th>
                <th className="px-[24px] py-[16px] text-center font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[13px] uppercase tracking-[0.275px]">
                  Total Score
                </th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((entry, index) => (
                <tr
                  key={entry.rank}
                  className={`border-b border-[#e4e8ee] ${index < 3 ? "bg-[rgba(0,88,171,0.02)]" : ""}`}
                >
                  <td className="px-[24px] py-[16px] text-center">
                    <div className="flex items-center justify-center">
                      {entry.rank <= 3 ? (
                        <div
                          className="size-[32px] rounded-full flex items-center justify-center font-['Inter:Semi_Bold',sans-serif] font-semibold text-[14px] text-white"
                          style={{
                            background:
                              entry.rank === 1
                                ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                                : entry.rank === 2
                                  ? "linear-gradient(135deg, #C0C0C0 0%, #808080 100%)"
                                  : "linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)",
                          }}
                        >
                          {entry.rank}
                        </div>
                      ) : (
                        <span className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#1a2332] text-[18px]">
                          {entry.rank}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-[24px] py-[16px]">
                    <p className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#0058ab] text-[14px]">
                      {entry.team}
                    </p>
                  </td>
                  <td className="px-[24px] py-[16px]">
                    <p className="font-['Inter:Medium',sans-serif] font-medium text-[#1a2332] text-[14px]">
                      {entry.idea}
                    </p>
                  </td>
                  <td className="px-[24px] py-[16px] text-center">
                    <span className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#4a5068] text-[14px]">
                      {entry.round1}
                    </span>
                  </td>
                  <td className="px-[24px] py-[16px] text-center">
                    <span className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#4a5068] text-[14px]">
                      {entry.round2}
                    </span>
                  </td>
                  <td className="px-[24px] py-[16px] text-center">
                    <span className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#4a5068] text-[14px]">
                      {entry.round3}
                    </span>
                  </td>
                  <td className="px-[24px] py-[16px] text-center">
                    <span className="font-['Inter:Semi_Bold',sans-serif] font-semibold text-[#00d68f] text-[18px]">
                      {entry.score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-[12px] justify-end mt-[24px]">
        <button className="px-[24px] py-[12px] bg-white border border-[#6e7891] text-[#6e7891] rounded font-['Inter:Medium',sans-serif] font-medium text-[14px] hover:bg-[#f5f7fb] transition-colors">
          Export CSV
        </button>
        <button className="px-[24px] py-[12px] bg-white border border-[#0058ab] text-[#0058ab] rounded font-['Inter:Medium',sans-serif] font-medium text-[14px] hover:bg-[#f0f5ff] transition-colors">
          Share Link
        </button>
        <button
          onClick={handlePublish}
          className="px-[24px] py-[12px] bg-[#0058ab] text-white rounded-[14px] font-['Inter:Medium',sans-serif] font-medium text-[14px] shadow-[0px_4px_12px_0px_rgba(0,88,171,0.25)] hover:bg-[#004a94] transition-colors"
        >
          {isPublished ? "Unpublish" : "Publish"} Leaderboard
        </button>
      </div>
    </div>
  );
}
